import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FaRegClone, FaTrash, FaBorderAll } from "react-icons/fa6";
import { SceneCanvas as SceneCanvasType, CanvasPrimitiveType } from "@ltypes/scene";
import Product from "@ltypes/product";
import CanvasStage from "./CanvasStage";
import Toolbox from "./Toolbox";
import PropertiesPanel from "./PropertiesPanel";
import LayersPanel from "./LayersPanel";
import {
    EditorPrimitive,
    withIds,
    stripIds,
    createPrimitive,
    nextId,
    DEVICE_WIDTH,
    DEVICE_HEIGHT,
} from "./primitives";
import { CanvasVariableContext } from "./variables";
import "./SceneCanvas.scss";

type SceneCanvasProps = {
    scene: SceneCanvasType,
    onChange: (field: string, value: any) => void,
};

function SceneCanvas(props: SceneCanvasProps) {
    const { t } = useTranslation();
    const product: Product | undefined = useSelector((state: any) => state.product);

    const [primitives, setPrimitives] = useState<Array<EditorPrimitive>>(() => withIds(props.scene.primitives ?? []));
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [showGrid, setShowGrid] = useState<boolean>(false);
    const [, setTick] = useState<number>(0);

    // Keep a live mirror so commit-on-drag-end reads the latest primitives.
    const primitivesRef = useRef<Array<EditorPrimitive>>(primitives);
    primitivesRef.current = primitives;

    // Re-render every second so live variables (time/date) animate on canvas.
    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, []);

    const ctx: CanvasVariableContext = useMemo(() => ({
        displayDurationMs: (props.scene.display_duration ?? 5) * 1000,
        elapsedMs: 0,
        brightness: 80,
        version: product?.version?.value ? String(product.version.value) : '2026.2',
    }), [props.scene.display_duration, product]);

    const pushUpstream = (next: Array<EditorPrimitive>) => props.onChange('primitives', stripIds(next));

    // Apply a new primitive list to local state, optionally persisting upstream.
    const apply = (next: Array<EditorPrimitive>, commit = true) => {
        setPrimitives(next);
        if (commit) pushUpstream(next);
    };

    const selected = primitives.find((p) => p._id === selectedId) || null;

    const handleCreateAt = (type: CanvasPrimitiveType, x: number, y: number) => {
        const p = createPrimitive(type, { x, y });
        apply([...primitivesRef.current, p]);
        setSelectedId(p._id);
    };

    const handleAddCenter = (type: CanvasPrimitiveType) => handleCreateAt(type, DEVICE_WIDTH / 2, DEVICE_HEIGHT / 2);

    // Live update (no upstream push) used during pointer drag.
    const handleUpdateLive = (id: string, p: EditorPrimitive) => {
        setPrimitives((prev) => prev.map((it) => (it._id === id ? p : it)));
    };

    // Commit the current local state upstream (drag end).
    const handleCommit = () => pushUpstream(primitivesRef.current);

    // Edit from panel/layers: update + persist immediately.
    const handleUpdate = (p: EditorPrimitive) => {
        apply(primitivesRef.current.map((it) => (it._id === p._id ? p : it)));
    };

    const handleDelete = (id: string) => {
        apply(primitivesRef.current.filter((p) => p._id !== id));
        if (selectedId === id) setSelectedId(null);
    };

    const moveCopyBy = (p: EditorPrimitive, dx: number, dy: number): EditorPrimitive => {
        if (p.type === 'line') return { ...p, pointA: { x: p.pointA.x + dx, y: p.pointA.y + dy }, pointB: { x: p.pointB.x + dx, y: p.pointB.y + dy } };
        if (p.type === 'triangle') return { ...p, pointA: { x: p.pointA.x + dx, y: p.pointA.y + dy }, pointB: { x: p.pointB.x + dx, y: p.pointB.y + dy }, pointC: { x: p.pointC.x + dx, y: p.pointC.y + dy } };
        return { ...p, position: { x: (p as any).position.x + dx, y: (p as any).position.y + dy } };
    };

    const handleDuplicate = (id: string) => {
        const src = primitivesRef.current.find((p) => p._id === id);
        if (!src) return;
        const copy = moveCopyBy({ ...(JSON.parse(JSON.stringify(src)) as EditorPrimitive), _id: nextId() }, 6, 6);
        const index = primitivesRef.current.findIndex((p) => p._id === id);
        const next = [...primitivesRef.current];
        next.splice(index + 1, 0, copy);
        apply(next);
        setSelectedId(copy._id);
    };

    const reorder = (id: string, direction: -1 | 1) => {
        const index = primitivesRef.current.findIndex((p) => p._id === id);
        const target = index + direction;
        if (index < 0 || target < 0 || target >= primitivesRef.current.length) return;
        const next = [...primitivesRef.current];
        [next[index], next[target]] = [next[target], next[index]];
        apply(next);
    };

    const nudge = (dx: number, dy: number) => {
        if (!selected) return;
        const moved = moveCopyBy(selected, dx, dy);
        apply(primitivesRef.current.map((it) => (it._id === selected._id ? moved : it)));
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (!selectedId) return;
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;

        if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            handleDelete(selectedId);
        } else if (e.key === 'Escape') {
            setSelectedId(null);
        } else if (e.key.startsWith('Arrow')) {
            e.preventDefault();
            const step = e.shiftKey ? 10 : 1;
            if (e.key === 'ArrowUp') nudge(0, -step);
            if (e.key === 'ArrowDown') nudge(0, step);
            if (e.key === 'ArrowLeft') nudge(-step, 0);
            if (e.key === 'ArrowRight') nudge(step, 0);
        }
    };

    return (
        <div className="scene-canvas" tabIndex={0} onKeyDown={onKeyDown}>
            <div className="scene-canvas-hint">{t('page.scenes.canvas.hint')}</div>

            <Toolbox onAdd={handleAddCenter} />

            <div className="scene-canvas-main">
                <div className="scene-canvas-stage-wrap">
                    <CanvasStage
                        primitives={primitives}
                        selectedId={selectedId}
                        ctx={ctx}
                        showGrid={showGrid}
                        onSelect={setSelectedId}
                        onUpdate={handleUpdateLive}
                        onCommit={handleCommit}
                        onCreateAt={handleCreateAt}
                    />
                    <div className="scene-canvas-stage-toolbar">
                        <button type="button" className={`scene-canvas-tool-btn ${showGrid ? 'active' : ''}`} title={t('page.scenes.canvas.grid')} onClick={() => setShowGrid((g) => !g)}>
                            <FaBorderAll size="1rem" />
                        </button>
                        <div className="scene-canvas-tool-spacer" />
                        <button type="button" className="scene-canvas-tool-btn" disabled={!selected} title={t('page.scenes.canvas.duplicate')} onClick={() => selected && handleDuplicate(selected._id)}>
                            <FaRegClone size="1rem" />
                        </button>
                        <button type="button" className="scene-canvas-tool-btn danger" disabled={!selected} title={t('page.scenes.canvas.delete')} onClick={() => selected && handleDelete(selected._id)}>
                            <FaTrash size="1rem" />
                        </button>
                    </div>
                </div>

                <PropertiesPanel
                    primitive={selected}
                    displayDuration={props.scene.display_duration ?? 5}
                    onChange={handleUpdate}
                    onChangeDuration={(s) => props.onChange('display_duration', s)}
                />
            </div>

            <LayersPanel
                primitives={primitives}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onRaise={(id) => reorder(id, 1)}
                onLower={(id) => reorder(id, -1)}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
            />
        </div>
    );
}

export default SceneCanvas;
